import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Container, Tab, Tabs, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { EmojiEventsRounded, VisibilityOffRounded } from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { fetchLeaderboard } from '../../services/GameSlice';
import { getUserId } from '../../identity';
import { getSocket } from '../../socket';
import './leaderboard.css';

// Games against the computer, ranked. Each player's best win at a level counts
// once: fewest moves first, then the quicker game. Moves are the player's own
// turns; time runs from the deal to the winning move. There is a board for
// today, this week and this month (calendar periods in UTC) and one for all
// time, and an open page updates itself as results come in.

const LEVELS = [
    { key: 'easy', label: 'Easy' },
    { key: 'medium', label: 'Medium' },
    { key: 'hard', label: 'Hard' }
];

const PERIODS = [
    { key: 'day', label: 'Today', title: 'today' },
    { key: 'week', label: 'This week', title: 'this week' },
    { key: 'month', label: 'This month', title: 'this month' },
    { key: 'all', label: 'All time', title: 'of all time' }
];

// "Resets in 5h 12m" — how long until the board starts again.
const untilReset = (resetsAt, now) => {
    const ms = new Date(resetsAt).getTime() - now;
    if (ms <= 0) return 'Resetting…';
    const minutes = Math.floor(ms / 60000);
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    if (days > 0) return `Resets in ${days}d ${hours}h`;
    if (hours > 0) return `Resets in ${hours}h ${minutes % 60}m`;
    return `Resets in ${Math.max(1, minutes)}m`;
};

const clock = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = String(seconds % 60).padStart(2, '0');
    return m >= 60 ? `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

const when = (date) => {
    const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
    if (days < 1) return 'Today';
    if (days < 2) return 'Yesterday';
    if (days < 30) return `${days} days ago`;
    return new Date(date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const Medal = ({ rank }) => (
    <span className={`lb-rank${rank <= 3 ? ` top top-${rank}` : ''}`}>{rank}</span>
);

const NoHints = () => (
    <Tooltip title="Every move made with the board's highlights switched off">
        <span className="lb-badge"><VisibilityOffRounded fontSize="inherit" />No highlights</span>
    </Tooltip>
);

const FastestRow = ({ row, fresh }) => (
    <tr className={[row.isYou ? 'lb-you' : '', fresh ? 'fresh' : ''].join(' ').trim() || undefined}>
        <td><Medal rank={row.rank} /></td>
        <td>
            <div className="lb-name">
                <span>{row.name}{row.isYou && <em> (you)</em>}</span>
                <span className="lb-tags">
                    {row.noHints && <NoHints />}
                    {row.opponents > 1 && <span className="lb-tag">vs {row.opponents}</span>}
                    {row.board === 'shuffled' && <span className="lb-tag">Shuffled</span>}
                </span>
            </div>
        </td>
        <td className="num strong">{row.moves}</td>
        <td className="num">{clock(row.seconds)}</td>
        <td className="lb-when">{when(row.finishedAt)}</td>
    </tr>
);

const Leaderboard = () => {
    const [params, setParams] = useSearchParams();
    const level = LEVELS.some(l => l.key === params.get('level')) ? params.get('level') : 'medium';
    const period = PERIODS.some(p => p.key === params.get('period')) ? params.get('period') : 'week';
    const [board, setBoard] = useState(null);
    const [failed, setFailed] = useState(false);
    // Rows that are new since the last look, lit up for a moment.
    const [fresh, setFresh] = useState(new Set());
    const [now, setNow] = useState(Date.now());
    const shown = useRef(null);

    const choose = (next) => setParams({ level, period, ...next }, { replace: true });

    const load = useCallback((quietly) => {
        if (!quietly) {
            setBoard(null);
            setFailed(false);
        }
        return fetchLeaderboard(level, getUserId(), period)
            .then(data => {
                const key = (row) => `${row.name}|${row.moves}|${row.seconds}`;
                const before = shown.current;
                setFresh(quietly && before ? new Set(data.fastest.map(key).filter(k => !before.has(k))) : new Set());
                shown.current = new Set(data.fastest.map(key));
                setBoard(data);
            })
            .catch(() => !quietly && setFailed(true));
    }, [level, period]);

    useEffect(() => {
        shown.current = null;
        load(false);
    }, [load]);

    // Live: the server says when a result comes in at some level; refresh if
    // it is this one. Also when the period rolls over while the page is open.
    useEffect(() => {
        const socket = getSocket();
        const watch = () => socket.emit('leaderboard:watch');
        let timer = null;
        const changed = ({ difficulty }) => {
            if (difficulty === level) {
                clearTimeout(timer);
                timer = setTimeout(() => load(true), 400);
            }
        };
        watch();
        socket.on('connect', watch);
        socket.on('leaderboard:changed', changed);
        return () => {
            clearTimeout(timer);
            socket.off('connect', watch);
            socket.off('leaderboard:changed', changed);
            socket.emit('leaderboard:unwatch');
        };
    }, [level, load]);

    useEffect(() => {
        const tick = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(tick);
    }, []);
    useEffect(() => {
        if (board?.resetsAt && new Date(board.resetsAt).getTime() <= now) {
            load(true);
        }
    }, [board, now, load]);
    useEffect(() => {
        if (fresh.size === 0) {
            return undefined;
        }
        const timer = setTimeout(() => setFresh(new Set()), 4000);
        return () => clearTimeout(timer);
    }, [fresh]);

    const label = LEVELS.find(l => l.key === level).label;
    const periodInfo = PERIODS.find(p => p.key === period);
    const youOutside = board?.you && !board.fastest.some(row => row.isYou);

    return (
        <Box>
            <Helmet>
                <title>Leaderboard - Fastest Wins Against the Computer | Play Sequence</title>
                <meta name="description" content="The fastest wins against the computer on Play Sequence, at Easy, Medium and Hard. Ranked by fewest moves, then time." />
            </Helmet>

            <Box className="lb-hero">
                <Container maxWidth="md">
                    <EmojiEventsRounded className="lb-hero-icon" aria-hidden="true" />
                    <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 1 }}>Leaderboard</Typography>
                    <Typography sx={{ color: 'rgba(243,238,226,0.75)', maxWidth: 560 }}>
                        The quickest wins against the computer. Each player's best win counts: fewest moves first, then the faster game.
                    </Typography>
                    <Tabs
                        value={level}
                        onChange={(event, value) => choose({ level: value })}
                        className="lb-tabs"
                        aria-label="Computer level"
                    >
                        {LEVELS.map(l => <Tab key={l.key} value={l.key} label={l.label} />)}
                    </Tabs>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: 4 }}>
                <div className="lb-periods">
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={period}
                        onChange={(event, value) => value && choose({ period: value })}
                        aria-label="Period"
                    >
                        {PERIODS.map(p => <ToggleButton key={p.key} value={p.key}>{p.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                    {board?.yourName && <span className="lb-whoami">You're <strong>{board.yourName}</strong></span>}
                    <span className="lb-live" title="Updates by itself as games finish">
                        <span className="lb-live-dot" aria-hidden="true" />Live
                        {board?.resetsAt && <span className="lb-reset">· {untilReset(board.resetsAt, now)}</span>}
                    </span>
                </div>
                {!board && !failed && <div className="lb-loading"><CircularProgress size={28} /></div>}
                {failed && <p className="lb-empty">The leaderboard couldn't be loaded. Check your connection and try again.</p>}

                {board && <>
                    <section className="lb-card" aria-labelledby="lb-fastest">
                        <div className="lb-card-head">
                            <h2 id="lb-fastest">Fastest wins · {label} · {periodInfo.label}</h2>
                            {board.totalPlayers > 0 && <span>{board.totalPlayers} {board.totalPlayers === 1 ? 'player' : 'players'}</span>}
                        </div>
                        {board.fastest.length === 0 ? (
                            <div className="lb-empty">
                                <p>{period === 'all' ? `Nobody has beaten the computer on ${label} yet.` : `Nobody has beaten the computer on ${label} ${periodInfo.title} yet.`}</p>
                                <Button component={Link} to="/" variant="contained">Be the first</Button>
                            </div>
                        ) : (
                            <div className="lb-scroll">
                                <table className="lb-table">
                                    <thead>
                                        <tr>
                                            <th scope="col" aria-label="Rank">#</th>
                                            <th scope="col">Player</th>
                                            <th scope="col" className="num">Moves</th>
                                            <th scope="col" className="num">Time</th>
                                            <th scope="col" className="lb-when">When</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {board.fastest.map(row => <FastestRow key={`${row.name}-${row.moves}-${row.seconds}`} row={row} fresh={fresh.has(`${row.name}|${row.moves}|${row.seconds}`)} />)}
                                        {youOutside && <>
                                            <tr className="lb-gap" aria-hidden="true"><td colSpan={5}>⋯</td></tr>
                                            <FastestRow row={board.you} />
                                        </>}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    {board.players.length > 0 && <section className="lb-card" aria-labelledby="lb-wins">
                        <div className="lb-card-head">
                            <h2 id="lb-wins">Most wins · {label} · {periodInfo.label}</h2>
                        </div>
                        <div className="lb-scroll">
                            <table className="lb-table">
                                <thead>
                                    <tr>
                                        <th scope="col" aria-label="Rank">#</th>
                                        <th scope="col">Player</th>
                                        <th scope="col" className="num">Wins</th>
                                        <th scope="col" className="num">Played</th>
                                        <th scope="col" className="num">Win rate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {board.players.map(row => (
                                        <tr key={row.rank} className={row.isYou ? 'lb-you' : undefined}>
                                            <td><Medal rank={row.rank} /></td>
                                            <td>
                                                <div className="lb-name">
                                                    <span>{row.name}{row.isYou && <em> (you)</em>}</span>
                                                    {row.noHintWins > 0 && <span className="lb-tags"><Tooltip title={`${row.noHintWins} of these wins with highlights off`}>
                                                        <span className="lb-badge"><VisibilityOffRounded fontSize="inherit" />{row.noHintWins}</span>
                                                    </Tooltip></span>}
                                                </div>
                                            </td>
                                            <td className="num strong">{row.wins}</td>
                                            <td className="num">{row.games}</td>
                                            <td className="num">{row.winRate}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>}

                    <p className="lb-note">
                        Everyone is shown under a guest name that stays the same on their browser; the name you type into a game is
                        only seen by the people in it. Every finished game against the computer counts: one player against computer opponents only. Today, this
                        week and this month start again at midnight UTC, on Mondays and on the 1st. Switch off
                        highlights next to your hand to earn the <VisibilityOffRounded fontSize="inherit" sx={{ verticalAlign: '-2px' }} /> badge.
                    </p>
                </>}
            </Container>
        </Box>
    );
};

export default Leaderboard;
